import React from "react";
import { Users, GraduationCap, Scale, ShieldCheck } from "lucide-react";

/**
 * SchoolStatsPanel — visual card displaying jumlah siswa, jumlah guru, and
 * teacher:student ratio for a school. Shown inside the "Informasi Umum" tab
 * on the SchoolDetail page.
 *
 * The ratio is auto-computed when both counts exist, otherwise falls back to
 * the manually-entered `teacher_to_student_ratio` string on the school doc.
 *
 * Also surfaces the government verification badge when the NPSN has been
 * verified via Kemdikbud (school.government_verified === true).
 */
export default function SchoolStatsPanel({ school }) {
  const students = Number(school.students_count) || 0;
  const teachers = Number(school.teachers_count) || 0;

  // Auto ratio when both counts present; fall back to manual string
  let ratioDisplay = school.teacher_to_student_ratio || "";
  let ratioValue = null; // numeric students-per-teacher, used for the density bar
  if (teachers > 0 && students > 0) {
    const perTeacher = students / teachers;
    ratioValue = perTeacher;
    ratioDisplay = `1 : ${Math.round(perTeacher)}`;
  }

  // Density bar: <15 excellent, 15-25 good, >25 crowded. Cap at 40 for visual scale.
  const barPct = ratioValue ? Math.min(100, (Math.min(ratioValue, 40) / 40) * 100) : 0;
  const densityLabel =
    ratioValue == null ? null :
    ratioValue <= 15 ? { text: "Ideal — kelas kecil", color: "text-[#065F46]", bar: "bg-[#059669]" } :
    ratioValue <= 25 ? { text: "Baik — standar nasional", color: "text-[#1E3A8A]", bar: "bg-[#1E3A8A]" } :
    { text: "Padat — kelas besar", color: "text-[#92400E]", bar: "bg-[#D97706]" };

  return (
    <section
      data-testid="school-stats-panel"
      className="bg-white border border-[#EAE7E0] rounded-2xl p-5"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#1E3A8A] font-semibold">
          <Users size={13} strokeWidth={2} /> Kepadatan Kelas & Tenaga Pengajar
        </div>
        {school.government_verified && (
          <span
            data-testid="school-stats-gov-badge"
            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-[#065F46] bg-[#ECFDF5] border border-[#A7F3D0] rounded-full px-2 py-0.5"
            title="Data NPSN diverifikasi ke referensi Kemdikbud"
          >
            <ShieldCheck size={10} strokeWidth={2.5} /> Data Kemdikbud
          </span>
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard
          icon={<Users size={18} strokeWidth={1.75} />}
          label="Jumlah siswa"
          value={students > 0 ? students.toLocaleString("id-ID") : "—"}
          accent="text-[#1E3A8A]"
          bg="bg-[#EFF6FF]"
          border="border-[#DBEAFE]"
          testid="stat-students"
        />
        <StatCard
          icon={<GraduationCap size={18} strokeWidth={1.75} />}
          label="Jumlah guru"
          value={teachers > 0 ? teachers.toLocaleString("id-ID") : "—"}
          accent="text-[#065F46]"
          bg="bg-[#ECFDF5]"
          border="border-[#A7F3D0]"
          testid="stat-teachers"
        />
        <StatCard
          icon={<Scale size={18} strokeWidth={1.75} />}
          label="Rasio guru : siswa"
          value={ratioDisplay || "—"}
          accent="text-[#B45309]"
          bg="bg-[#FEF3C7]"
          border="border-[#FDE68A]"
          testid="stat-ratio"
        />
      </div>

      {densityLabel && (
        <div className="mt-4" data-testid="stat-density-bar">
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className="text-[#4B5563]">Rata-rata siswa per guru</span>
            <span className={"font-semibold " + densityLabel.color}>{densityLabel.text}</span>
          </div>
          <div className="h-2 rounded-full bg-[#F3F1ED] overflow-hidden">
            <div
              className={"h-full transition-all duration-500 " + densityLabel.bar}
              style={{ width: `${barPct}%` }}
              data-testid="stat-density-fill"
            />
          </div>
          <div className="mt-1 flex items-center justify-between text-[10px] text-[#6B7280] uppercase tracking-widest">
            <span>1 : 5</span>
            <span>1 : 20</span>
            <span>1 : 40</span>
          </div>
        </div>
      )}

      <p className="mt-4 text-[11px] text-[#6B7280] leading-relaxed">
        Kepadatan kelas menggambarkan seberapa banyak siswa per satu guru — makin kecil rasionya, makin mudah guru memperhatikan tiap anak.
      </p>
    </section>
  );
}

function StatCard({ icon, label, value, accent, bg, border, testid }) {
  return (
    <div className={`rounded-xl border ${border} ${bg} p-4 flex flex-col gap-1`} data-testid={testid}>
      <div className={`inline-flex items-center gap-1.5 ${accent}`}>{icon}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#4B5563] font-semibold">{label}</div>
      <div className={`font-serif-display text-3xl leading-none mt-1 ${accent}`}>{value}</div>
    </div>
  );
}
